import React from 'react';

import { StyleSheet } from 'react-native';
import type { ViewStyle } from 'react-native';

import { Box, Typography, withTypography } from 'components/ui';
import type { BoxProps, TypographyProps } from 'components/ui';
import { getDesignToken, spacing } from 'styles/constants';

export type TotalsRowSpacing = keyof typeof spacing | number;

type TotalsRowProps = BoxProps & {
  justifyContent?: ViewStyle['justifyContent'];
  mb?: TotalsRowSpacing;
  mt?: TotalsRowSpacing;
};

const getSpacingValue = (value?: TotalsRowSpacing) => {
  if (typeof value === 'number') {
    return value;
  }

  return value ? spacing[value] : undefined;
};

const getTotalsRowStyle = ({
  justifyContent,
  mb,
  mt,
}: Pick<TotalsRowProps, 'justifyContent' | 'mb' | 'mt'>): ViewStyle | undefined => {
  const marginBottom = getSpacingValue(mb);
  const marginTop = getSpacingValue(mt);
  const style: ViewStyle = {};

  if (justifyContent) {
    style.justifyContent = justifyContent;
  }

  if (marginBottom !== undefined) {
    style.marginBottom = marginBottom;
  }

  if (marginTop !== undefined) {
    style.marginTop = marginTop;
  }

  return Object.keys(style).length ? style : undefined;
};

export const FeeDeliveryItem = withTypography({
  color: getDesignToken('alert-warning-background'),
  bold: true,
});

export const Total = ({ focusable = true, ...props }: BoxProps) =>
  React.createElement(Box, { focusable, ...props });

export const GrandTotalLabel = withTypography({
  color: getDesignToken('text-default'),
  fontSize: 'md',
  bold: true,
  style: { flexGrow: 1 },
});

export const TotalLabel = ({
  isFreeDelivery,
  color,
  style,
  ...props
}: TypographyProps & { isFreeDelivery?: boolean; color?: string }) =>
  React.createElement(Typography, {
    color:
      color ||
      (isFreeDelivery
        ? getDesignToken('alert-warning-background')
        : getDesignToken('text-default')),
    fontSize: 'md',
    bold: !!isFreeDelivery,
    style: [{ flexGrow: 1 }, style],
    ...props,
  });

export const DonationLabel = withTypography({
  color: getDesignToken('text-default'),
});

export const TotalLabelContainer = ({ focusable = true, style, ...props }: BoxProps) =>
  React.createElement(Box, {
    focusable,
    ...props,
    style: [styles.totalLabelContainer, style],
  });

export const TotalsRow = ({ justifyContent, mb, mt, style, ...props }: TotalsRowProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.totalsRow, getTotalsRowStyle({ justifyContent, mb, mt }), style],
  });

const styles = StyleSheet.create({
  totalLabelContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    height: spacing.$6,
  },
  totalsRow: {
    flexDirection: 'row',
  },
});
